import API_BASE from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from "react-hot-toast";

const ACCENT = '#1A56A0';

const DESTINATIONS = [
  { value: 'pipedrive_csv',  label: 'Pipedrive (CSV)' },
  { value: 'hubspot_csv',    label: 'HubSpot (CSV)' },
  { value: 'bigin_csv',      label: 'Zoho Bigin (CSV)' },
  { value: 'zoho_crm_csv',   label: 'Zoho CRM (CSV)' },
  { value: 'salesforce_csv', label: 'Salesforce (CSV)' },
];

function destinationLabel(value) {
  return DESTINATIONS.find(d => d.value === value)?.label || value;
}

export default function EmailBatches() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [client, setClient] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDest, setNewDest] = useState('pipedrive_csv');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchContacts, setBatchContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  async function authFetch(url, opts = {}) {
    const token = await getToken();
    return fetch(url, {
      ...opts,
      headers: {
        ...(opts.headers || {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  async function loadClient() {
    try {
      const res = await authFetch(`${API_BASE}/clients/${id}`);
      if (res.ok) setClient(await res.json());
    } catch (e) { /* noop */ }
  }

  async function loadBatches() {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/clients/${id}/email-batches`);
      if (!res.ok) throw new Error('Failed to load batches');
      setBatches(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadBatchContacts(batchId) {
    setContactsLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}/contacts`);
      setBatchContacts(res.ok ? await res.json() : []);
    } catch (e) {
      setBatchContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }

  useEffect(() => { loadClient(); loadBatches(); }, [id]);
  useEffect(() => { if (selectedBatch) loadBatchContacts(selectedBatch.id); }, [selectedBatch]);

  async function createBatch() {
    if (!newName.trim()) { toast.error('Batch name required'); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API_BASE}/clients/${id}/email-batches`, {
        method: 'POST',
        body: JSON.stringify({ name: newName.trim(), destination_type: newDest }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create batch');
      }
      setNewName('');
      toast.success('Batch created');
      await loadBatches();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function renameBatch(batchId, currentName) {
    const next = window.prompt('Rename batch', currentName);
    if (!next || !next.trim() || next.trim() === currentName) return;
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}`, {
        method: 'PUT',
        body: JSON.stringify({ name: next.trim() }),
      });
      if (!res.ok) throw new Error('Rename failed');
      toast.success('Renamed');
      await loadBatches();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function deleteBatch(batchId, name) {
    if (!window.confirm(`Delete batch "${name}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Batch deleted');
      if (selectedBatch?.id === batchId) setSelectedBatch(null);
      await loadBatches();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function exportBatch(batchId) {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/email-batches/${batchId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Export failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-batch-${batchId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      await loadBatches();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function removeContact(batchId, contactId) {
    if (!window.confirm('Remove this contact from the batch?')) return;
    try {
      const res = await authFetch(`${API_BASE}/email-batches/${batchId}/contacts/${contactId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Remove failed');
      toast.success('Contact removed');
      await loadBatchContacts(batchId);
      await loadBatches();
    } catch (e) {
      toast.error(e.message);
    }
  }

  function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function StatusPill({ status }) {
    const map = {
      draft:    { label: 'Draft',    color: '#B7791F', bg: '#FFFBEB' },
      exported: { label: 'Exported', color: '#276749', bg: '#F0FFF4' },
    };
    const s = map[status] || map.draft;
    return (
      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: s.color, backgroundColor: s.bg, padding: '0.15rem 0.55rem', borderRadius: '20px' }}>
        {s.label}
      </span>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <button
        onClick={() => navigate(`/clients/${id}`)}
        style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}
      >
        ← Back to workspace
      </button>

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.6rem' }}>Email Batches</h1>
        {client && <span style={{ color: '#888', fontSize: '0.9rem' }}>{client.name}</span>}
      </div>
      <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0, marginBottom: '2rem' }}>
        Group contacts with approved email sequences and export as a CSV ready to import into your CRM.
      </p>

      <div style={{ background: '#FFF', border: '1px solid #EEE', borderRadius: '10px', padding: '1.25rem', marginBottom: '2rem' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.6rem' }}>Create new batch</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px auto', gap: '0.6rem' }}>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createBatch(); }}
            placeholder="Batch name (eg. Q4 UK accountants)"
            style={{ padding: '0.55rem 0.75rem', border: '1px solid #DDD', borderRadius: '6px', fontSize: '0.9rem' }}
          />
          <select
            value={newDest}
            onChange={(e) => setNewDest(e.target.value)}
            style={{ padding: '0.55rem 0.75rem', border: '1px solid #DDD', borderRadius: '6px', fontSize: '0.9rem', background: '#FFF' }}
          >
            {DESTINATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <button
            onClick={createBatch}
            disabled={creating}
            style={{ background: ACCENT, color: '#FFF', border: 'none', padding: '0.55rem 1.1rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.6 : 1 }}
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#888' }}>Loading batches…</div>
      ) : batches.length === 0 ? (
        <div style={{ background: '#FAFAFA', border: '1px dashed #DDD', borderRadius: '10px', padding: '2.5rem', textAlign: 'center', color: '#888' }}>
          No batches yet. Create one above to get started.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {batches.map((b) => (
            <div
              key={b.id}
              style={{ background: '#FFF', border: '1px solid #EEE', borderRadius: '10px', padding: '1rem 1.25rem' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{b.name}</span>
                    <StatusPill status={b.status} />
                    <span style={{ fontSize: '0.72rem', color: '#888', background: '#F4F4F4', padding: '0.1rem 0.5rem', borderRadius: '20px' }}>
                      {destinationLabel(b.destination_type)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#888' }}>
                    {b.contact_count} {Number(b.contact_count) === 1 ? 'contact' : 'contacts'} · created {formatDate(b.created_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setSelectedBatch(selectedBatch?.id === b.id ? null : b)}
                    style={{ background: '#FFF', color: '#333', border: '1px solid #DDD', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    {selectedBatch?.id === b.id ? 'Hide' : 'View'}
                  </button>
                  <button
                    onClick={() => renameBatch(b.id, b.name)}
                    style={{ background: '#FFF', color: '#333', border: '1px solid #DDD', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    Rename
                  </button>
                  <button
                    onClick={() => deleteBatch(b.id, b.name)}
                    style={{ background: '#FFF', color: '#C53030', border: '1px solid #FEB2B2', padding: '0.4rem 0.8rem', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => exportBatch(b.id)}
                    disabled={Number(b.contact_count) === 0}
                    style={{
                      background: Number(b.contact_count) === 0 ? '#EEE' : ACCENT,
                      color: Number(b.contact_count) === 0 ? '#999' : '#FFF',
                      border: 'none',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: Number(b.contact_count) === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              {selectedBatch?.id === b.id && (
                <div style={{ marginTop: '1rem', borderTop: '1px solid #EEE', paddingTop: '1rem' }}>
                  {contactsLoading ? (
                    <div style={{ color: '#888', fontSize: '0.85rem' }}>Loading contacts…</div>
                  ) : batchContacts.length === 0 ? (
                    <div style={{ color: '#888', fontSize: '0.85rem' }}>
                      No contacts in this batch yet. Add contacts from the Prospects screen.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.4rem' }}>
                      {batchContacts.map((bc) => (
                        <div
                          key={bc.id}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: '#FAFAFA', borderRadius: '6px', fontSize: '0.85rem' }}
                        >
                          <div>
                            <span style={{ fontWeight: 500 }}>{bc.full_name || '—'}</span>
                            <span style={{ color: '#888', marginLeft: '0.5rem' }}>
                              {bc.email ? `· ${bc.email}` : ''}
                              {bc.company_name ? ` · ${bc.company_name}` : ''}
                            </span>
                          </div>
                          <button
                            onClick={() => removeContact(b.id, bc.contact_id)}
                            style={{ background: 'none', border: 'none', color: '#999', fontSize: '0.78rem', cursor: 'pointer' }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
